import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { QrCode, ParkingCircle, AlertTriangle, Wifi, WifiOff, Maximize, Monitor } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Heartbeat interval (every 60 seconds)
const HEARTBEAT_INTERVAL = 60000;
// Playlist refresh interval (every 45 seconds)
const PLAYLIST_REFRESH_INTERVAL = 45000;

export default function ScreenPlayer() {
  const { screenId } = useParams();
  const [playbackData, setPlaybackData] = useState(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastHeartbeat, setLastHeartbeat] = useState(null);
  const [isOnline, setIsOnline] = useState(true);
  const containerRef = useRef(null);
  const timerRef = useRef(null);
  const videoRef = useRef(null);

  // Fetch playback data from API
  const fetchPlayback = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/playback/screen/${screenId}`);
      if (res.ok) {
        const data = await res.json();
        setPlaybackData(data);
        setError(null);
        setIsOnline(true);
      } else if (res.status === 404) {
        setError("Screen not found or inactive");
        setPlaybackData(null);
      } else {
        setError("Failed to load content");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Connection error");
      setIsOnline(false);
    } finally {
      setLoading(false);
    }
  }, [screenId]);

  // Send heartbeat to server
  const sendHeartbeat = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/screens/${screenId}/heartbeat`, {
        method: "POST"
      });
      if (res.ok) {
        setLastHeartbeat(new Date());
        setIsOnline(true);
      }
    } catch (err) {
      console.error("Heartbeat error:", err);
      setIsOnline(false);
    }
  }, [screenId]);

  // Initial load
  useEffect(() => {
    fetchPlayback();
    sendHeartbeat();

    // Set up intervals
    const playbackInterval = setInterval(fetchPlayback, PLAYLIST_REFRESH_INTERVAL);
    const heartbeatInterval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

    return () => {
      clearInterval(playbackInterval);
      clearInterval(heartbeatInterval);
    };
  }, [fetchPlayback, sendHeartbeat]);

  // Content rotation timer
  useEffect(() => {
    if (!playbackData?.items?.length) return;

    const currentItem = playbackData.items[currentItemIndex];
    if (!currentItem) return;

    // Clear existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // For videos, wait for video to end
    if (currentItem.content_type === "video") {
      // Video will trigger next via onEnded
      return;
    }

    // For other content, use duration
    const duration = (currentItem.duration_seconds || 10) * 1000;
    timerRef.current = setTimeout(() => {
      setCurrentItemIndex((prev) => (prev + 1) % playbackData.items.length);
    }, duration);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [currentItemIndex, playbackData]);

  // Handle video end
  const handleVideoEnd = () => {
    if (playbackData?.items?.length) {
      setCurrentItemIndex((prev) => (prev + 1) % playbackData.items.length);
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Render content item
  const renderContent = (item) => {
    if (!item) return null;

    switch (item.content_type) {
      case "image":
        return (
          <div className="w-full h-full flex items-center justify-center bg-black">
            <img
              src={`${API_URL}${item.media_url}`}
              alt={item.name}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        );

      case "video":
        return (
          <div className="w-full h-full flex items-center justify-center bg-black">
            <video
              ref={videoRef}
              src={`${API_URL}${item.media_url}`}
              autoPlay
              muted
              onEnded={handleVideoEnd}
              className="max-w-full max-h-full"
            />
          </div>
        );

      case "widget":
        return renderWidget(item);

      default:
        return (
          <div className="w-full h-full flex items-center justify-center bg-slate-900 text-white">
            <p>Unsupported content type: {item.content_type}</p>
          </div>
        );
    }
  };

  // Render widget based on type
  const renderWidget = (item) => {
    const config = item.widget_config || {};
    const baseStyle = {
      backgroundColor: config.background_color || "#1e293b",
      color: config.text_color || "#ffffff"
    };

    const fontSizeMap = {
      small: "text-2xl md:text-3xl",
      medium: "text-3xl md:text-5xl",
      large: "text-4xl md:text-6xl",
      xlarge: "text-5xl md:text-8xl"
    };
    const titleSize = fontSizeMap[config.font_size] || fontSizeMap.large;

    switch (item.widget_type) {
      case "text_announcement":
        return (
          <div
            className="w-full h-full flex flex-col items-center justify-center p-8 md:p-16"
            style={baseStyle}
          >
            {config.title && (
              <h1 className={`${titleSize} font-bold text-center mb-6 md:mb-12 animate-fade-in`}>
                {config.title}
              </h1>
            )}
            {config.message && (
              <p className="text-xl md:text-3xl text-center opacity-90 max-w-4xl animate-fade-in-delay">
                {config.message}
              </p>
            )}
          </div>
        );

      case "parking_message":
        return (
          <div
            className="w-full h-full flex flex-col items-center justify-center p-8 md:p-16"
            style={baseStyle}
          >
            <ParkingCircle className="w-24 h-24 md:w-40 md:h-40 mb-8 animate-pulse" />
            <h1 className={`${titleSize} font-bold text-center mb-6`}>
              {config.title || "Parking Status"}
            </h1>
            <p className="text-2xl md:text-4xl text-center opacity-90">
              {config.message || "Designated spaces available"}
            </p>
            {config.spaces_available !== undefined && (
              <div className="mt-8 text-6xl md:text-9xl font-bold animate-bounce-slow">
                {config.spaces_available}
              </div>
            )}
          </div>
        );

      case "participation_prompt":
        return (
          <div
            className="w-full h-full flex flex-col items-center justify-center p-8 md:p-16"
            style={baseStyle}
          >
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16">
              <div className="flex flex-col items-center">
                <QrCode className="w-32 h-32 md:w-64 md:h-64 mb-4" />
                <p className="text-lg md:text-2xl opacity-70">Scan to participate</p>
              </div>
              <div className="text-center md:text-left">
                <h1 className={`${titleSize} font-bold mb-4`}>
                  {config.prompt_text || "Earn Parking Credits!"}
                </h1>
                <p className="text-xl md:text-3xl opacity-90">
                  Visit participating retailers and scan their QR codes
                </p>
                {config.show_credits_info && (
                  <p className="text-lg md:text-2xl mt-4 opacity-70">
                    Credits can be redeemed for free parking
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div
            className="w-full h-full flex flex-col items-center justify-center p-8"
            style={baseStyle}
          >
            <p className="text-2xl">Widget: {item.widget_type}</p>
          </div>
        );
    }
  };

  // Render emergency overlay
  const renderEmergencyBanner = () => {
    if (!playbackData?.is_emergency) return null;

    return (
      <div className="absolute top-0 left-0 right-0 bg-red-600 text-white py-4 px-6 flex items-center justify-center gap-4 z-50 animate-pulse">
        <AlertTriangle className="w-8 h-8" />
        <span className="text-xl md:text-2xl font-bold">
          {playbackData.emergency_message || "EMERGENCY ALERT"}
        </span>
        <AlertTriangle className="w-8 h-8" />
      </div>
    );
  };

  // Render no content fallback
  const renderNoContent = () => (
    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-8">
      <Monitor className="w-24 h-24 md:w-32 md:h-32 text-slate-600 mb-8" />
      <h1 className="text-3xl md:text-5xl font-bold text-center mb-4">
        {playbackData?.screen_name || "Digital Display"}
      </h1>
      <p className="text-xl md:text-2xl text-slate-400 text-center">
        {playbackData?.site_name || ""}
      </p>
      <p className="text-lg text-slate-500 mt-8">No content assigned</p>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-slate-900 text-white">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-400 mb-4" />
          <p className="text-xl">Loading display...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !playbackData) {
    return (
      <div className="w-screen h-screen flex flex-col items-center justify-center bg-slate-900 text-white p-8">
        <AlertTriangle className="w-20 h-20 text-amber-400 mb-6" />
        <h1 className="text-3xl font-bold mb-4">Display Error</h1>
        <p className="text-xl text-slate-400 mb-8">{error}</p>
        <p className="text-sm text-slate-500">Screen ID: {screenId}</p>
        <button
          onClick={fetchPlayback}
          className="mt-8 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-lg transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const currentItem = playbackData?.items?.[currentItemIndex];
  const hasContent = playbackData?.items?.length > 0;

  return (
    <div
      ref={containerRef}
      className="w-screen h-screen bg-black overflow-hidden relative cursor-none"
      onClick={toggleFullscreen}
    >
      {/* Emergency Banner */}
      {renderEmergencyBanner()}

      {/* Main Content */}
      <div className={`w-full h-full ${playbackData?.is_emergency ? 'pt-16' : ''}`}>
        {hasContent ? renderContent(currentItem) : renderNoContent()}
      </div>

      {/* Status Indicator (hidden in fullscreen, visible on hover) */}
      <div className="absolute bottom-4 right-4 flex items-center gap-3 opacity-0 hover:opacity-100 transition-opacity duration-300 cursor-pointer">
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isOnline ? 'bg-emerald-600/80' : 'bg-red-600/80'} text-white text-sm`}>
          {isOnline ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
          <span>{isOnline ? 'Online' : 'Offline'}</span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
          className="p-2 bg-slate-800/80 hover:bg-slate-700/80 rounded-lg text-white"
        >
          <Maximize className="w-5 h-5" />
        </button>
      </div>

      {/* Progress Indicator */}
      {hasContent && playbackData.items.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
          {playbackData.items.map((_, index) => (
            <div
              key={index}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentItemIndex ? 'bg-white scale-125' : 'bg-white/30'
              }`}
            />
          ))}
        </div>
      )}

      {/* Debug Info (only in development, hidden by default) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute top-4 left-4 text-white text-xs bg-black/50 p-2 rounded opacity-0 hover:opacity-100 transition-opacity">
          <p>Screen: {playbackData?.screen_name}</p>
          <p>Site: {playbackData?.site_name}</p>
          <p>Playlist: {playbackData?.playlist_name || 'None'}</p>
          <p>Item: {currentItemIndex + 1}/{playbackData?.items?.length || 0}</p>
          <p>Emergency: {playbackData?.is_emergency ? 'YES' : 'No'}</p>
          <p>Last heartbeat: {lastHeartbeat?.toLocaleTimeString()}</p>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        .animate-fade-in-delay {
          animation: fade-in 0.8s ease-out 0.3s forwards;
          opacity: 0;
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        /* Hide cursor after 3 seconds of inactivity */
        .cursor-none:not(:hover) {
          cursor: none;
        }
      `}</style>
    </div>
  );
}
